import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, X, Package } from 'lucide-react';
import { useResources, useResourceActions, useBoards } from '../stores';
import { fuzzyFilter, highlightMatches } from '../boardv3/utils/fuzzySearch';
import { MultiSelectDropdown, type DropdownOption } from '../components/MultiSelectDropdown';
import { HighlightedText } from '../components/HighlightedText';
import { ResourceModal } from './components/ResourceModal';
import { PageContainer, PageHeader, PageEmptyState } from '../components/PageLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { relativeDate } from '../lib/dateUtils';
import { getScopeLabel } from '../lib/scopeLabel';
import type { ResourceDefinition, BoardDefinition } from '../types';

function ResourceRow({
  resource,
  boardMap,
  searchQuery,
  onDelete,
  onSelect,
}: {
  resource: ResourceDefinition;
  boardMap: Map<string, BoardDefinition>;
  searchQuery: string;
  onDelete: (resourceId: string) => void;
  onSelect: (resourceId: string) => void;
}) {
  const { t } = useTranslation();
  const titleSegments = highlightMatches(searchQuery, resource.name || t('resource.newResource'));

  return (
    <tr
      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={() => onSelect(resource.id)}
    >
      <td className="px-3 py-1.5 text-xs text-gray-900 max-w-[280px]">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: resource.color || '#6366f1' }}
          />
          <span className="truncate font-medium">
            <HighlightedText segments={titleSegments} />
          </span>
        </div>
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-600">
        {resource.category || '-'}
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-600">
        {resource.fields.length}
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-500">
        {getScopeLabel(resource.scope, boardMap, { global: t('resource.scopeGlobal'), board: t('resource.scopeBoard'), canvas: t('resource.scopeCanvas') })}
      </td>
      <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">
        {relativeDate(resource.updatedAt)}
      </td>
      <td className="px-3 py-1.5 text-right">
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(resource.id); }}
          className="p-1 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

export function ResourcesPage() {
  useDocumentTitle('Resources \u2013 qtplan');
  const { t } = useTranslation();
  const resources = useResources();
  const actions = useResourceActions();
  const boards = useBoards();

  const [modalOpen, setModalOpen] = useState(false);
  const [editResourceId, setEditResourceId] = useState<string | undefined>(undefined);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterScopes, setFilterScopes] = useState<string[]>([]);

  const boardMap = useMemo(() => {
    const map = new Map<string, BoardDefinition>();
    for (const b of boards) map.set(b.id, b);
    return map;
  }, [boards]);

  const categoryOptions: DropdownOption[] = useMemo(() => {
    const cats = new Set(resources.map(r => r.category).filter(Boolean));
    return Array.from(cats).sort().map(c => ({ value: c, label: c }));
  }, [resources]);

  const scopeOptions: DropdownOption[] = useMemo(() => {
    const opts: DropdownOption[] = [
      { value: 'global', label: t('resource.scopeGlobal') },
    ];
    for (const b of boards) {
      opts.push({ value: `board:${b.id}`, label: b.name });
    }
    const hasCanvas = resources.some(r => r.scope.type === 'canvas');
    if (hasCanvas) {
      opts.push({ value: 'canvas', label: t('resource.scopeCanvas') });
    }
    return opts;
  }, [t, boards, resources]);

  const filteredResources = useMemo(() => {
    let result = resources;

    if (filterCategories.length > 0) {
      result = result.filter(r => filterCategories.includes(r.category));
    }

    if (filterScopes.length > 0) {
      result = result.filter(r => {
        if (r.scope.type === 'global') return filterScopes.includes('global');
        if (r.scope.type === 'board') return filterScopes.includes(`board:${r.scope.boardId}`);
        if (r.scope.type === 'canvas') return filterScopes.includes('canvas') || filterScopes.includes(`board:${r.scope.boardId}`);
        return false;
      });
    }

    if (searchQuery.trim()) {
      result = fuzzyFilter(result, searchQuery, (r) => `${r.name} ${r.category}`);
    }

    return result;
  }, [resources, searchQuery, filterCategories, filterScopes]);

  const handleOpenCreate = useCallback(() => {
    setEditResourceId(undefined);
    setModalOpen(true);
  }, []);

  const handleOpenEdit = useCallback((resourceId: string) => {
    setEditResourceId(resourceId);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditResourceId(undefined);
  }, []);

  const handleDelete = useCallback((resourceId: string) => {
    if (!confirm(t('resource.deleteConfirm'))) return;
    actions.deleteResource(resourceId);
  }, [t, actions]);

  const hasActiveFilters = filterCategories.length > 0 || filterScopes.length > 0;

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setFilterCategories([]);
    setFilterScopes([]);
  }, []);

  return (
    <PageContainer wide>
      <PageHeader
        title={t('nav.resources')}
        subtitle={
          filteredResources.length !== resources.length
            ? t('resource.overview.subtitleFiltered', { count: resources.length, filtered: filteredResources.length })
            : t('resource.overview.subtitle', { count: resources.length })
        }
        buttonLabel={t('resource.overview.addButton')}
        onButtonClick={handleOpenCreate}
      />

      {resources.length === 0 && !hasActiveFilters ? (
        <PageEmptyState
          icon={<Package size={20} />}
          title={t('resource.overview.noResources')}
          description={t('resource.overview.noResourcesDesc')}
        />
      ) : (
        <>
          <div className="mb-4">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('resource.overview.searchPlaceholder')}
                className="w-full pl-8 pr-7 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {categoryOptions.length > 0 && (
              <MultiSelectDropdown
                options={categoryOptions}
                selected={filterCategories}
                onChange={setFilterCategories}
                placeholder={t('resource.category')}
              />
            )}
            <MultiSelectDropdown
              options={scopeOptions}
              selected={filterScopes}
              onChange={setFilterScopes}
              placeholder={t('resource.scope')}
            />
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                {t('common.resetFilters')}
              </button>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('resource.name')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('resource.category')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('resource.field.label')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      {t('resource.scope')}
                    </th>
                    <th className="px-3 py-1.5 text-left text-[10px] font-medium text-gray-500">
                      Updated
                    </th>
                    <th className="px-3 py-1.5 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {filteredResources.map((resource) => (
                    <ResourceRow
                      key={resource.id}
                      resource={resource}
                      boardMap={boardMap}
                      searchQuery={searchQuery}
                      onDelete={handleDelete}
                      onSelect={handleOpenEdit}
                    />
                  ))}
                  {filteredResources.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-400">
                        {resources.length === 0
                          ? t('resource.overview.noResources')
                          : t('resource.overview.noResults')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ResourceModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        resourceId={editResourceId}
      />
    </PageContainer>
  );
}
